import * as React from 'react';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLocation } from '@xing-com/crate-core-router';
import { useViewerData } from '@xing-com/hub';

import { useHostName } from './use-host-name';
import { useIsCoverLetterEditor } from './use-is-cover-letter-editor';

type OptionalNWTTrackingParams = {
  event?: string;
  context?: string;
  element?: string;
  elementDetail?: string;
  page?: string;
  flags?: string;
  sentBy?: string;
  position?: number;
  itemUrn?: string;
  itemActorUrn?: string;
};

type LegacyInteractionParams = {
  trackAction?: string;
  pageName?: string;
};

type UseVitaTrackingProps = LegacyInteractionParams;

type UseVitaTrackingReturn = {
  trackWithNWT: (extraData?: OptionalNWTTrackingParams) => void;
  getTrackingPrefixes: () => {
    host: string;
    cover_letter: string;
    cv: string;
  };
};

export const useVitaTracking = ({
  trackAction: _trackAction,
  pageName,
}: UseVitaTrackingProps = {}): UseVitaTrackingReturn => {
  const { track: trackNwt } = useTracking();
  const userId = useViewerData().data?.user?.id;
  const hostName = useHostName();
  const isCoverLetterEditor = useIsCoverLetterEditor();
  const location = useLocation();
  const isCvEditor = location.pathname.includes('neu');
  const isXingHost = hostName.includes('xing');
  const isLebenslaufHost = hostName.includes('lebenslauf');
  const isAnschreibenHost = hostName.includes('anschreiben');
  const host = isAnschreibenHost ? 'anschreiben' : 'lebenslauf';

  const prefix = React.useMemo(() => {
    if (isAnschreibenHost) return 'ans';
    if (isXingHost) return 'wbm';
    if (isLebenslaufHost) return 'leb';
    else return 'unknown';
  }, [isAnschreibenHost, isXingHost, isLebenslaufHost]);
  const channel = React.useMemo(() => {
    return prefix + '/' + host;
  }, [prefix, host]);

  const trackWithNWT = React.useCallback<UseVitaTrackingReturn['trackWithNWT']>(
    (extraData) => {
      const application = prefix;

      let page = `${host}/${pageName}`;

      if (isAnschreibenHost && isCvEditor) {
        page = `${host}/lebenslauf/${pageName}`;
      }

      if (!isAnschreibenHost && isCoverLetterEditor) {
        page = `${host}/anschreiben/${pageName}`;
      }

      trackNwt({
        type: 'nwt',
        event: 'viewed_screen',
        eventSchema: 'basic',
        schemaVersion: '1.17.0',
        sentBy: host,
        channel,
        application,
        page,
        userId,
        ...extraData,
      });
    },
    [
      channel,
      host,
      isAnschreibenHost,
      isCoverLetterEditor,
      isCvEditor,
      pageName,
      prefix,
      trackNwt,
      userId,
    ]
  );

  const getTrackingPrefixes = (): {
    host: string;
    cover_letter: string;
    cv: string;
  } => {
    const prefixes = {
      host: isAnschreibenHost ? 'anschreiben' : 'lebenslauf',
      cover_letter: isAnschreibenHost
        ? 'anschreiben'
        : 'lebenslauf_anschreiben',
      cv: isAnschreibenHost ? 'anschreiben_lebenslauf' : 'lebenslauf',
    };

    return prefixes;
  };

  return {
    getTrackingPrefixes,
    trackWithNWT,
  };
};
